import { z } from 'zod';
import { cents, dateKey, optionalText, paginationQuery } from './common';
import { PAYMENT_METHOD_TYPES } from '../settings';

export const PURCHASE_STATUSES = ['draft', 'ordered', 'received', 'canceled'] as const;
export type PurchaseStatusKey = (typeof PURCHASE_STATUSES)[number];

export const PURCHASE_STATUS_LABELS: Record<PurchaseStatusKey, string> = {
  draft: 'Rascunho',
  ordered: 'Confirmada',
  received: 'Recebida',
  canceled: 'Cancelada',
};

/** Limites: uma nota com mais de 500 linhas não é uma nota, é uma importação. */
export const PURCHASE_MAX_ITEMS = 500;
export const PURCHASE_MAX_INSTALLMENTS = 60;

const qty = z.number().positive('Informe a quantidade').max(1_000_000);

export const purchaseItemInputSchema = z
  .object({
    /** Id da linha existente (edição); ausente = linha nova. */
    id: z.string().uuid().optional(),
    productId: z.string().uuid().nullish(),
    variantId: z.string().uuid().nullish(),
    /** Descrição como veio do fornecedor (obrigatória mesmo com produto vinculado). */
    description: z.string().trim().min(1, 'Descreva o item').max(200),
    supplierCode: optionalText(60),
    barcode: optionalText(60),
    unit: z.string().trim().min(1).max(10).default('un'),
    quantity: qty,
    unitCostCents: cents,
    /** Desconto da LINHA (o da nota inteira vai em discountCents do cabeçalho). */
    discountCents: cents.default(0),
    /**
     * Valor bruto exato da linha (vProd da NF-e). Opcional: sem ele vale quantidade × custo
     * unitário. Serve para notas com custo unitário de mais de 2 casas (12,3333).
     */
    grossCents: cents.nullish(),
  })
  .refine((v) => !v.variantId || v.productId, { message: 'Escolha o produto da variação.', path: ['productId'] });
export type PurchaseItemInput = z.infer<typeof purchaseItemInputSchema>;

export const purchaseInstallmentSchema = z.object({
  dueDate: dateKey,
  amountCents: cents.refine((v) => v > 0, 'Informe o valor da parcela'),
  /** Parcela já paga no ato (entrada): nasce baixada no Financeiro. */
  paid: z.boolean().default(false),
});
export type PurchaseInstallmentInput = z.infer<typeof purchaseInstallmentSchema>;

export const purchaseInputSchema = z.object({
  supplierId: z.string().uuid().nullish(),
  invoiceNumber: optionalText(20),
  /** Chave de acesso da NF-e (44 dígitos, pontuação ignorada). */
  invoiceKey: z
    .string()
    .trim()
    .nullish()
    .transform((v) => (v ? v.replace(/\D/g, '') : null))
    .refine((v) => v === null || v.length === 44, 'A chave de acesso tem 44 números'),
  /** Data de emissão da nota (dia, no fuso da loja). */
  issueDate: dateKey.nullish(),
  /** Previsão de entrega. */
  expectedDate: dateKey.nullish(),
  freightCents: cents.default(0),
  /** Desconto no total da nota (rateado entre as linhas no custo). */
  discountCents: cents.default(0),
  /** Outras despesas da nota (seguro, IPI, ICMS-ST...), rateadas no custo. */
  otherCostsCents: cents.default(0),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES).nullish(),
  /** Parcelas: vazio = à vista (uma conta a pagar com o total). Soma tem de fechar com o total. */
  installments: z.array(purchaseInstallmentSchema).max(PURCHASE_MAX_INSTALLMENTS).default([]),
  notes: optionalText(1000),
  items: z.array(purchaseItemInputSchema).max(PURCHASE_MAX_ITEMS, 'Até 500 itens por compra').default([]),
});
export type PurchaseInput = z.infer<typeof purchaseInputSchema>;

/** PATCH: só o que vier no corpo muda. `items`, quando vier, substitui TODAS as linhas. */
export const purchaseUpdateSchema = purchaseInputSchema.partial();
export type PurchaseUpdateInput = z.infer<typeof purchaseUpdateSchema>;

export const purchaseListQuery = paginationQuery.extend({
  status: z.enum(PURCHASE_STATUSES).optional(),
  supplierId: z.string().uuid().optional(),
  /** Período pela data de emissão (ou de criação, quando sem emissão). */
  from: dateKey.optional(),
  to: dateKey.optional(),
  /** Número da compra, número/chave da nota, fornecedor ou item. */
  q: z.string().trim().max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(30),
});
export type PurchaseListQuery = z.infer<typeof purchaseListQuery>;

/**
 * Recebimento. Sem `items`: recebe tudo o que falta. Com `items`: `receivedQty` é o TOTAL
 * recebido da linha depois desta operação (não o incremento), o que torna o POST repetido
 * inofensivo: receber "5 de 10" duas vezes continua dando 5.
 */
export const purchaseReceiveSchema = z.object({
  items: z
    .array(z.object({ itemId: z.string().uuid(), receivedQty: z.number().min(0).max(1_000_000) }))
    .max(PURCHASE_MAX_ITEMS)
    .optional(),
  /** Encerra o recebimento mesmo com itens pendentes (o fornecedor não vai entregar o resto). */
  finish: z.boolean().default(false),
});
export type PurchaseReceiveInput = z.infer<typeof purchaseReceiveSchema>;

export const purchaseCancelSchema = z.object({ reason: optionalText(300) });

export const purchaseImportNfeSchema = z.object({
  xml: z.string().min(1, 'Envie o XML da nota').max(2 * 1024 * 1024, 'O XML passa de 2 MB'),
  /** Cadastra o fornecedor a partir do emitente quando o CNPJ ainda não existe (padrão: sim). */
  createSupplier: z.boolean().default(true),
});
export type PurchaseImportNfeInput = z.infer<typeof purchaseImportNfeSchema>;

export const applyPricesSchema = z.object({
  items: z
    .array(
      z.object({
        productId: z.string().uuid(),
        /** Com variação: preço próprio da variação. Sem: preço do produto (herdado pelas variações sem preço próprio). */
        variantId: z.string().uuid().nullish(),
        priceCents: cents.refine((v) => v > 0, 'O preço precisa ser maior que zero'),
      }),
    )
    .min(1, 'Informe ao menos um preço')
    .max(PURCHASE_MAX_ITEMS),
});
export type ApplyPricesInput = z.infer<typeof applyPricesSchema>;
